import { useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { COLLECTION_INITIAL_VALUES } from '@constants'

import TextArea from '@components/shared/TextArea'
import { Message } from '@components/shared/Message'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import Checkbox from '$components/Forms/Checkbox'
import ModalHeader from '../shared/CustomModal/ModalHeader'
import ModalBody from '../shared/CustomModal/ModalBody'
import ModalFooter from '../shared/CustomModal/ModalFooter'
import { MdInfoOutline } from 'react-icons/md'

export const UpsertCollection = ({
    showAdd,
    showHideAdd,
    editCollection,
    createRunsheetAfterwards,
    setCreateRunsheetAfterwards,
    onSuccess,
}) => {
    
    const [message, setMessage] = useState()
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false)
    
    const collections = useWireValue(store.collections)
    const collectionsNames = useMemo(() => collections?.rows?.map(({ name }) => name) ?? [], [collections])
    const pendingCollection = useMemo(() => editCollection ? {
        id: editCollection?.id || '',
        userId: editCollection?.userId || '',
        state: editCollection?.state || '',
        name: editCollection?.name || '',
        description: editCollection?.description || '',
        creatorId: editCollection?.creatorId || '',
        county: editCollection?.county || '',
    } : null, [editCollection])
    
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: pendingCollection || COLLECTION_INITIAL_VALUES,
        
        onSubmit: async values => {
            
            const collectionName = collectionsNames.filter(name => name === values.name)
            
            if (!values.name)
                return setMessage({ text: 'Please fill in the collection name', level: 'error' })
            
            if (collectionName.length && !editCollection)
                return setMessage({ text: 'Error: Duplicate collection name', level: 'error' })
            
            const collection = {
                userId: localStorage.getItem('userId') || '',
                creatorId: values.creatorId,
                name: values.name,
                state: values.state,
                county: values.county,
                description: values.description,
            }
            
            setIsSaveButtonDisabled(true)
            
            let newCollection
            
            if (!editCollection)
                newCollection = await actions.createCollection(collection)
            else
                newCollection = await actions.updateCollection({ id: editCollection.id, ...collection })
            
            onSuccess(newCollection)
            
            setIsSaveButtonDisabled(false)
            
        },
    })
    
    const { handleSubmit, handleChange, values } = formik
    
    const handleModal = () => {
        showHideAdd(false)
    }
    
    return (
        <CustomModal open={showAdd} modalHandler={() => handleModal()} center>
            <form onSubmit={handleSubmit}>
                <ModalHeader>{editCollection ? 'Edit Collection' : 'Add Collection'}</ModalHeader>
                <ModalBody className="flex flex-col gap-4">
                    {message && <Message message={message} />}
                    <div className="flex items-center justify-between">
                        <label>Name</label>
                        <input
                            type="text"
                            name="name"
                            className="input w-10/12 ml-auto"
                            value={values.name}
                            onChange={handleChange}
                            placeholder="Collection Name"
                            data-test-id="UpsertCollectionCollectionNameInput" />
                    </div>
                    <div className="flex items-center justify-between">
                        <label>State</label>
                        <input
                            type="text"
                            name="state"
                            className="input w-10/12 ml-auto"
                            value={values.state}
                            onChange={handleChange}
                            placeholder="State" />
                    </div>
                    <div className="flex items-center justify-between">
                        <label>County</label>
                        <input
                            type="text"
                            name="county"
                            className="input w-10/12 ml-auto"
                            value={values.county}
                            onChange={handleChange}
                            placeholder="County" />
                    </div>
                    <div className="flex items-start justify-between">
                        <label>Description</label>
                        <TextArea
                            name="description"
                            className="input w-10/12 !min-h-16"
                            placeholder="Collection description..."
                            onChange={handleChange}
                            value={values.description} />
                    </div>
                </ModalBody>
                <ModalFooter>
                    {!editCollection &&
                        <div className="flex flex-1 flex-row items-center gap-1">
                            <Checkbox
                                checked={createRunsheetAfterwards}
                                onChange={e => setCreateRunsheetAfterwards(e.target.checked)}
                                label="Create Runsheet" />
                            <span className="tooltip"
                                data-tip="Check this box to create a runsheet after creating the collection">
                                <MdInfoOutline />
                            </span>
                        </div>
                    }
                    <button
                        type="button"
                        className="btn btn-primary btn-outline"
                        onClick={() => {
                            handleModal()
                        }}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={!values.name || isSaveButtonDisabled}
                        data-test-id="UpsertCollectionSaveButton">
                        {editCollection ? 'Update' : 'Create'}
                    </button>
                </ModalFooter>
            </form>
        </CustomModal>
    )
}
